// components/ui/ModalShell.tsx
// UI Phase 2.5: low-level modal shell for the bespoke modals (AddCourse,
// UnifiedCourse, SemesterManagement, APCredits, EnrollmentSettings,
// RequirementChange, HierarchicalProgram). Unlike BaseModal (which owns the
// header/footer layout), ModalShell renders children raw — the modal keeps
// its custom header/tabs/footer and only swaps its hand-rolled overlay +
// card divs for a Radix-backed shell: focus trap/restore, scroll lock,
// Escape, aria, and the warm token surface come from here.
// Backdrop-click semantics match BaseModal: only a click directly on the
// backdrop closes; isLoading blocks Escape/backdrop dismissal.

import { useRef } from 'react';
import type { ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { cn } from './cn';

export interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name for the dialog (custom headers aren't Radix Titles) */
  ariaLabel: string;
  /** Tailwind max-width class, e.g. 'max-w-4xl' */
  maxWidth?: string;
  /** Blocks Escape + backdrop dismissal while true */
  isLoading?: boolean;
  /** Blocks dismissal without implying loading (e.g. a nested dialog is open) */
  blockDismiss?: boolean;
  /** id of an element describing the dialog. When set, it becomes the dialog's
   *  accessible description and the default hidden Description is suppressed
   *  (used by BaseModal to wire its subtitle). */
  describedBy?: string;
  /** Extra classes on the dialog surface (e.g. 'max-h-[90vh] flex flex-col') */
  className?: string;
  /** Extra classes on the backdrop wrapper (e.g. responsive padding) */
  overlayClassName?: string;
  children: ReactNode;
}

const ModalShell = ({
  open,
  onClose,
  ariaLabel,
  maxWidth = 'max-w-md',
  isLoading = false,
  blockDismiss = false,
  describedBy,
  className,
  overlayClassName,
  children,
}: ModalShellProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  return (
    <RadixDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !isLoading && !blockDismiss) onClose();
      }}
    >
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          data-testid="modal-backdrop"
          className={cn('fixed inset-0 z-50 bg-black/50 animate-fade-in', overlayClassName)}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isLoading && !blockDismiss) onClose();
          }}
        />
        <RadixDialog.Content
          ref={contentRef}
          aria-label={ariaLabel}
          aria-describedby={describedBy}
          aria-modal="true"
          tabIndex={-1}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            previouslyFocused.current = document.activeElement as HTMLElement | null;
            contentRef.current?.focus();
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            previouslyFocused.current?.focus?.();
          }}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            if (isLoading || blockDismiss) e.preventDefault();
          }}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[calc(100vw-2rem)] bg-surface-card rounded-surface shadow-lg border border-line-soft',
            // Centering-aware keyframes: plain `animate-scale-in` would drop the
            // -translate-*-1/2 centering for the animation's duration and flash
            // the modal in the bottom-right quadrant before it snapped to center.
            'animate-scale-in-centered focus:outline-none',
            maxWidth,
            className
          )}
        >
          {/* Radix warns without a Description or aria-describedby; custom headers
              carry the visible title. When the caller supplies describedBy, that
              satisfies Radix and this hidden fallback is unnecessary. */}
          {!describedBy && <RadixDialog.Description className="hidden" />}
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

export default ModalShell;
